import { FC } from "react";

import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { BasicWidgetHorizontal } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetHorizontal.tsx";
import { BasicWidgetVertical } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetVertical.tsx";
import { PropertyValueRendererParams } from "src/dynamic-view-renderer/Widgets/PropertyRenderer.tsx";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import { WidgetContainerProps } from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { BlacklistedCaseIdentifier, CaseSummary } from "src/types/case";
import { ClientSchema } from "src/types/clientSchema";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { useGetWidgetProperties } from "../WidgetProperties";

export type BasicWidgetProps = {
  data?: {
    case: {
      propertyValues?: PropertyValue[];
      elements?: Element[];
      blacklistedIdentifiers?: BlacklistedCaseIdentifier[];
      customProperties?: WidgetProperty[];
      source?: CaseSummary | null;
      code?: string | null;
    };
    propertyValueRendererParams?: PropertyValueRendererParams;
  };
  caseSchema?: ClientSchema;
} & Omit<WidgetContainerProps, "children">;

const DEFAULT_ORIENTATION = "vertical";

const BasicWidget: FC<BasicWidgetProps> = ({ data, caseSchema, ...widgetContainerAttributes }) => {
  const uiSchema = useDynamicViewSchema();
  const getWidgetProperties = useGetWidgetProperties();

  const propertyDefinitions = uiSchema.propertyDefinitions;

  const widgetDefinition = widgetContainerAttributes.widgetDefinition;
  const propertyValueRendererParams = data?.propertyValueRendererParams;

  const orientation =
    widgetDefinition?.configuration?.orientation === "horizontal"
      ? "horizontal"
      : widgetDefinition?.configuration?.orientation === "vertical"
      ? "vertical"
      : DEFAULT_ORIENTATION;

  let widgetProperties = getWidgetProperties({
    caseSchema,
    widgetDefinition,
    data: data,
    propertyDefinitions,
  });

  // Add custom properties if they exist
  if (data?.case?.customProperties) {
    widgetProperties = [...data.case.customProperties, ...widgetProperties];
  }

  const basicWidgetAttributes = {
    properties: widgetProperties,
    propertyValueRendererParams,
    ...widgetContainerAttributes,
  };

  return orientation === "vertical" ? (
    <BasicWidgetVertical {...basicWidgetAttributes} />
  ) : (
    <BasicWidgetHorizontal {...basicWidgetAttributes} />
  );
};

export default BasicWidget;
